import React, { useState, useEffect } from "react";
import Slider, { Settings } from "react-slick";
import {
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";

// Carousel styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { ArticleCard } from "features/feed/ArticleCard";
import { ArticleSkeleton } from "features/feed/ArticleSkeleton";
import { apiSrcUrl } from "utils";

// A simple arrow component for the slider
const SlickArrow = ({ onClick, isNext = false }: any) => (
  <div
    className={`absolute top-1/2 -translate-y-1/2 cursor-pointer z-10 ${
      isNext ? "right-[-20px]" : "left-[-20px]"
    }`}
    onClick={onClick}
  >
    {isNext ? (
      <ArrowForwardIos fontSize="large" color="action" />
    ) : (
      <ArrowBackIosNew fontSize="large" color="action" />
    )}
  </div>
);

// Type Definitions
interface Article {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  imageUrl: string;
}

type ViewType = "grid" | "slider";

interface RssFeedProps {
  search: string;
  defaultView?: ViewType;
  showViewOptions?: boolean;
}
export const RssFeed: React.FC<RssFeedProps> = ({
  search,
  defaultView = "slider",
  showViewOptions = true,
}) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<ViewType>(defaultView);
  const isMobile = useMediaQuery("(max-width:599px)");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${apiSrcUrl}/public/feed?q=${encodeURIComponent(search)}`
        );
        const data = await response.json();
        setArticles(data || []);
        updateSlidesToShow(window.innerWidth, data?.length || 0);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [search]);

  const handleViewChange = (
    _: React.MouseEvent<HTMLElement>,
    newView: ViewType | null
  ) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  const sliderSettings: Settings = {
    dots: true,
    infinite: articles.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: !isMobile,
    swipeToSlide: true,
    nextArrow: <SlickArrow isNext />,
    prevArrow: <SlickArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: articles.length > 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: articles.length > 1,
        },
      },
    ],
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <ArticleSkeleton key={index} />
          ))}
        </div>
      );
    }

    if (articles.length === 0) {
      return (
        <div className="flex w-full h-48 justify-center items-center">
          <Typography variant="h6" color="text.secondary">
            No articles found.
          </Typography>
        </div>
      );
    }

    if (viewType === "slider") {
      return (
        <div className="mx-8">
          <Slider {...sliderSettings}>
            {articles.map((article, index) => (
              <ArticleCard key={index} article={article} />
            ))}
          </Slider>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {articles.map((article, index) => (
          <ArticleCard key={index} article={article} />
        ))}
      </div>
    );
  };

  return (
    <div className="p-4 w-full bg-gray-50 min-h-screen">
      <div className="container mx-auto">
        {showViewOptions && !loading && articles.length > 0 && (
          <div className="flex justify-center md:justify-end mb-6">
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={handleViewChange}
              aria-label="view type"
            >
              <ToggleButton value="grid" aria-label="grid view">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="slider" aria-label="slider view">
                <ViewListIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
};
function updateSlidesToShow(innerWidth: number, arg1: any) {
  throw new Error("Function not implemented.");
}
